import {axi, getBackendAssetPath} from '@shared/api';
import {TranslationDict} from '@shared/types';
import {toUtcFromTz, validateData} from '@shared/utils';
import {Banner, BannerDetail} from '../types';
import {
  adminBannerDetailV8nSchema,
  v8nSchemaOfGetAdminBannersResponse,
} from './backendSchemas';
import {
  AdminBannerDetailOnBackend,
  GetAdminBannersResponse,
} from './backendTypes';
import {
  mapAdminBannerDetailFromBackend,
  mapAdminBannerFromBackend,
} from './dataMappers';

export interface GetAdminBannersParams {
  geos?: string[];
  onlyActive?: boolean;
}

export const getAdminBanners = async ({
  geos,
  onlyActive,
}: GetAdminBannersParams = {}): Promise<Banner[]> => {
  const response = await axi.get<GetAdminBannersResponse>('/admin/banners', {
    params: {
      geos: geos?.length ? geos : undefined,
      only_active: onlyActive,
    },
    paramsSerializer: {
      indexes: null,
    },
  });

  try {
    validateData(
      response.data,
      v8nSchemaOfGetAdminBannersResponse,
      'getAdminBanners',
    );
  } catch {
    //
  }

  return (response.data ?? []).map(item => mapAdminBannerFromBackend(item));
};

export const getAdminBanner = async (id: string): Promise<BannerDetail> => {
  const response = await axi.get<AdminBannerDetailOnBackend>(
    `/admin/banners/${id}`,
  );

  try {
    validateData(response.data, adminBannerDetailV8nSchema, 'getAdminBanner');
  } catch {
    //
  }

  return mapAdminBannerDetailFromBackend(response.data);
};

const cleanTranslations = (
  trans: TranslationDict,
): Partial<TranslationDict> => {
  const result: Partial<TranslationDict> = {};
  Object.entries(trans).forEach(([lang, val]) => {
    if (typeof val === 'string' && val.trim()) {
      result[lang as keyof TranslationDict] = val;
    }
  });
  return result;
};

export interface CreateBannerParams {
  nameTrans: TranslationDict;
  logoUrl: string;
  appUrl?: string | null;
  webUrl?: string | null;
  activeFrom?: Date | null;
  activeTo?: Date | null;
  geos?: string[];
  order?: number;
}

export interface UpdateBannerParams extends CreateBannerParams {
  id: string;
}

const buildBannerPayload = ({
  nameTrans,
  logoUrl,
  appUrl,
  webUrl,
  activeFrom,
  activeTo,
  geos,
  order,
}: CreateBannerParams) => ({
  name: cleanTranslations(nameTrans),
  logo_url: getBackendAssetPath(logoUrl),
  app_url: appUrl || null,
  web_url: webUrl || null,
  active_from: activeFrom ? toUtcFromTz(activeFrom).toISOString() : null,
  active_to: activeTo ? toUtcFromTz(activeTo).toISOString() : null,
  geos: geos ?? [],
  order: order ?? 0,
});

export const createBanner = async (
  params: CreateBannerParams,
): Promise<void> => {
  await axi.post('/admin/banners', buildBannerPayload(params));
};

export const updateBanner = async ({
  id,
  ...params
}: UpdateBannerParams): Promise<void> => {
  await axi.put(`/admin/banners/${id}`, buildBannerPayload(params));
};
